/**
 * FAQModal — Preguntas Frecuentes oficiales de DamyxIA (CUADRO NEGRO feb-2026).
 *
 * Modal grande con acordeón colapsable. Bilingüe ES/EN. Cierre por ✕, Escape
 * o click en el backdrop. Cada FAQItem es expandible/colapsable independientemente.
 *
 * Marcelo decidió que el contenido vive directo en el componente (no en un JSON
 * separado) para que sea fácil ajustar copy sin pasar por traducciones globales.
 */
import { useEffect, useRef, useState } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { createPortal } from 'react-dom';
import './FAQModal.css';

function FAQItem({ question, children, defaultOpen = false, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="faq-item" data-testid={testId}>
      <button
        type="button"
        className="faq-item-question"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <span>{question}</span>
        <ChevronDown
          className={`faq-item-chevron ${open ? 'open' : ''}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div className="faq-item-answer" data-testid={testId ? `${testId}-body` : undefined}>
          {children}
        </div>
      )}
    </div>
  );
}

export default function FAQModal({ isOpen, onClose }) {
  const modalRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    if (modalRef.current) {
      try { modalRef.current.focus(); } catch { /* ignore */ }
    }
    // Bloquear scroll del body mientras está abierto
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Detectar idioma de forma simple (LanguageContext si está disponible, sino ES por default).
  const lang = (typeof localStorage !== 'undefined' && localStorage.getItem('damyxia.language')) || 'es';
  const es = lang === 'es';

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const content = (
    <div
      className="faq-modal-backdrop"
      onClick={handleBackdrop}
      data-testid="faq-modal-backdrop"
    >
      <div
        ref={modalRef}
        className="faq-modal"
        role="dialog"
        aria-modal="true"
        aria-label={es ? 'Preguntas Frecuentes' : 'Frequently Asked Questions'}
        tabIndex={-1}
        data-testid="faq-modal"
      >
        <div className="faq-modal-header">
          <h2>{es ? 'Preguntas Frecuentes' : 'Frequently Asked Questions'}</h2>
          <button
            type="button"
            className="faq-modal-close"
            onClick={onClose}
            aria-label={es ? 'Cerrar' : 'Close'}
            data-testid="faq-modal-close"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        <div className="faq-modal-body">
          <FAQItem
            defaultOpen
            testId="faq-item-can"
            question={es ? '¿Qué puede hacer DamyxIA?' : 'What can DamyxIA do?'}
          >
            <p>{es
              ? 'DamyxIA puede:'
              : 'DamyxIA can:'}</p>
            <ul>
              <li>{es ? 'Generar imágenes a partir de texto, ideas o referencias.' : 'Generate images from text, ideas or references.'}</li>
              <li>{es ? 'Crear variaciones y mejoras de imágenes existentes.' : 'Create variations and enhance existing images.'}</li>
              <li>{es ? 'Ayudarte a explorar estilos visuales y conceptos creativos.' : 'Help you explore visual styles and creative concepts.'}</li>
              <li>{es ? 'Mantener un historial de tus creaciones recientes.' : 'Keep a history of your recent creations.'}</li>
              <li>{es ? 'Organizar tu trabajo dentro de un área de trabajo (Workspace).' : 'Organize your work inside a Workspace.'}</li>
              <li>{es ? 'Acompañarte con diálogos guiados para aclarar ideas y pasos.' : 'Walk you through guided dialogs to clarify ideas and steps.'}</li>
            </ul>
            <p>{es
              ? 'En resumen: DamyxIA es una herramienta de creación visual y asistencia inteligente, pensada para ayudarte a imaginar, crear y organizar.'
              : 'In short: DamyxIA is a visual creation and intelligent assistance tool designed to help you imagine, create and organize.'}</p>
          </FAQItem>

          <FAQItem testId="faq-item-cannot" question={es ? '¿Qué no puede hacer DamyxIA?' : 'What can DamyxIA NOT do?'}>
            <p>{es ? 'DamyxIA NO puede:' : 'DamyxIA CANNOT:'}</p>
            <ul>
              <li>{es ? 'Crear aplicaciones completas (frontend + backend + base de datos).' : 'Build complete apps (frontend + backend + database).'}</li>
              <li>{es ? 'Hacer deploy de proyectos en internet o crear dominios.' : 'Deploy projects to the internet or create domains.'}</li>
              <li>{es ? 'Ejecutar código arbitrario del usuario en servidores propios.' : 'Run arbitrary user code on its own servers.'}</li>
              <li>{es ? 'Auto-modificarse o cambiar su propio código en producción.' : 'Self-modify or change its own production code.'}</li>
              <li>{es ? 'Reemplazar por completo a un equipo de desarrollo o diseño.' : 'Fully replace a development or design team.'}</li>
            </ul>
            <p>{es
              ? 'Preferimos ser claros: DamyxIA es potente, pero no es mágica. Tiene límites definidos para evitar falsas expectativas y decepciones.'
              : 'We prefer to be clear: DamyxIA is powerful but not magic. It has defined limits to avoid false expectations.'}</p>
          </FAQItem>

          <FAQItem testId="faq-item-limits" question={es ? '¿Cómo funciona el límite de imágenes?' : 'How does the image limit work?'}>
            <ul>
              <li>{es ? 'En el Plan Free tenés una cantidad mensual de imágenes disponibles.' : 'The Free Plan gives you a monthly image quota.'}</li>
              <li>{es ? 'Ese límite se renueva cada mes y no se acumula.' : 'The limit renews monthly and does not stack.'}</li>
              <li>{es ? 'Cada vez que generás una imagen, se descuenta del saldo disponible.' : 'Each image you generate is deducted from your available balance.'}</li>
              <li>{es ? 'Si llegás al límite, podés esperar al próximo ciclo o pasarte al Plan Premium para uso sin límites (dentro de lo razonable).' : 'If you reach the limit, you can wait for next cycle or upgrade to Premium for unlimited (fair) use.'}</li>
            </ul>
            <p>{es
              ? 'El objetivo es que siempre sepas cuántos recursos estás usando y que no haya sorpresas.'
              : 'The goal is that you always know how much you are using, with no surprises.'}</p>
          </FAQItem>

          <FAQItem testId="faq-item-actions" question={es ? '¿Cómo funcionan las Acciones Rápidas?' : 'How do Quick Actions work?'}>
            <p>{es ? 'Las Acciones Rápidas son atajos que te permiten:' : 'Quick Actions are shortcuts that let you:'}</p>
            <ul>
              <li>{es ? 'Ejecutar tareas comunes con un solo click.' : 'Run common tasks with one click.'}</li>
              <li>{es ? 'Aplicar transformaciones frecuentes sin escribir instrucciones largas.' : 'Apply frequent transformations without long prompts.'}</li>
              <li>{es ? 'Repetir patrones de uso que ya te funcionan bien.' : 'Repeat usage patterns that work well for you.'}</li>
            </ul>
            <p>{es ? 'Ejemplos típicos: "Mejorar esta imagen", "Crear variaciones", "Cambiar estilo".' : 'Typical examples: "Enhance this image", "Create variations", "Change style".'}</p>
          </FAQItem>

          <FAQItem testId="faq-item-upload" question={es ? '¿Cómo funciona Cargar o Arrastrar Imágenes?' : 'How does Upload or Drag Images work?'}>
            <ul>
              <li>{es ? 'Podés arrastrar una imagen desde tu computadora hacia el área indicada.' : 'You can drag an image from your computer onto the indicated area.'}</li>
              <li>{es ? 'O hacer click en "Cargar imagen" y seleccionar un archivo.' : 'Or click "Upload image" and pick a file.'}</li>
            </ul>
            <p>{es ? 'Una vez cargada: DamyxIA puede usarla como referencia, editarla o mejorarla. Se guarda en tu historial o Workspace según el flujo.' : 'Once uploaded: DamyxIA can use it as reference, edit it or enhance it. It is saved to your history or Workspace.'}</p>
            <p>{es ? 'Siempre se respeta tu contenido: no se comparte con otros usuarios.' : 'Your content is always respected: it is not shared with other users.'}</p>
          </FAQItem>

          <FAQItem testId="faq-item-written-images" question={es ? '¿Cómo funciona Subir Imágenes Escritas?' : 'How does Upload Written Images work?'}>
            <p>{es ? '"Imágenes Escritas" se refiere a imágenes que contienen texto (capturas, notas, bocetos) o contenido visual que querés transformar.' : '"Written Images" means images containing text (screenshots, notes, sketches) or visuals you want to transform.'}</p>
            <ul>
              <li>{es ? 'Subilas como cualquier otra imagen.' : 'Upload them like any other image.'}</li>
              <li>{es ? 'Pedile a DamyxIA que las reinterprete.' : 'Ask DamyxIA to reinterpret them.'}</li>
              <li>{es ? 'Generá nuevas versiones basadas en la idea original.' : 'Generate new versions based on the original idea.'}</li>
            </ul>
          </FAQItem>

          <FAQItem testId="faq-item-mic" question={es ? '¿Cómo funciona el Micrófono?' : 'How does the Microphone work?'}>
            <ul>
              <li>{es ? 'Podés hablar en lugar de escribir.' : 'You can speak instead of typing.'}</li>
              <li>{es ? 'DamyxIA convierte tu voz en texto.' : 'DamyxIA converts your voice to text.'}</li>
              <li>{es ? 'Ese texto se usa como instrucción para generar imágenes o contenido.' : 'That text is used as instruction to generate images or content.'}</li>
            </ul>
            <p>{es ? 'Siempre se te pedirá permiso antes de usar el micrófono.' : 'You will always be asked for permission before the microphone is used.'}</p>
          </FAQItem>

          <FAQItem testId="faq-item-dialogs" question={es ? '¿Cómo funcionan los Diálogos?' : 'How do Dialogs work?'}>
            <ul>
              <li>{es ? 'Podés hacer preguntas, pedir cambios, aclarar ideas.' : 'Ask questions, request changes, clarify ideas.'}</li>
              <li>{es ? 'DamyxIA responde en contexto, recordando lo conversado en la sesión.' : 'DamyxIA replies in context, remembering the session.'}</li>
              <li>{es ? 'Podés refinar resultados paso a paso.' : 'Refine results step by step.'}</li>
            </ul>
            <p>{es ? 'La idea es que sientas que trabajás con un asistente creativo, no solo con un generador automático.' : 'The idea is that you feel you are working with a creative assistant, not just an automatic generator.'}</p>
          </FAQItem>

          <FAQItem testId="faq-item-area" question={es ? '¿Cómo funciona el Área de Trabajo?' : 'How does the Work Area work?'}>
            <ul>
              <li>{es ? 'Ves tus imágenes recientes.' : 'See your recent images.'}</li>
              <li>{es ? 'Organizás tus creaciones.' : 'Organize your creations.'}</li>
              <li>{es ? 'Podés seleccionar, comparar y decidir qué conservar.' : 'Select, compare and decide what to keep.'}</li>
            </ul>
            <p>{es ? 'Es tu mesa de trabajo visual dentro de DamyxIA.' : 'It is your visual desk inside DamyxIA.'}</p>
          </FAQItem>

          <FAQItem testId="faq-item-workspace" question={es ? '¿Cómo funciona el Workspace?' : 'How does the Workspace work?'}>
            <ul>
              <li>{es ? 'Podés agrupar proyectos.' : 'Group projects together.'}</li>
              <li>{es ? 'Mantener versiones relacionadas.' : 'Keep related versions.'}</li>
              <li>{es ? 'Volver a estados anteriores.' : 'Revert to previous states.'}</li>
              <li>{es ? 'Tener una vista más organizada de tu proceso creativo.' : 'Get a more organized view of your creative process.'}</li>
            </ul>
            <p>{es ? 'Ideal cuando trabajás en varias ideas o proyectos a la vez.' : 'Ideal when you work on several ideas or projects at once.'}</p>
          </FAQItem>

          <FAQItem testId="faq-item-agenda" question={es ? '¿Cómo funciona la Agenda y Pedir Ayuda?' : 'How do the Agenda and Get Help work?'}>
            <ul>
              <li>{es ? 'Permite comunicar Ideas, Inquietudes o Recomendar a DamyxIA.' : 'Lets you communicate Ideas, Concerns or Recommend things to DamyxIA.'}</li>
              <li>{es ? 'Tener un pequeño Recordatorio Comunicativo dentro de DamyxIA.' : 'Keep a small Communication Reminder inside DamyxIA.'}</li>
              <li>{es ? '"Pedir Ayuda".' : '"Get Help".'}</li>
            </ul>
          </FAQItem>

          <FAQItem testId="faq-item-trash" question={es ? '¿Cómo funciona la Papelera?' : 'How does the Trash work?'}>
            <p>{es ? 'La Papelera es donde van las imágenes o archivos que eliminás. Según tu plan:' : 'The Trash holds images or files you delete. By plan:'}</p>
            <ul>
              <li>{es ? 'Plan Free: visible pero con acceso limitado; recuperación con costo por imagen o acceso temporal.' : 'Free Plan: visible but limited; recovery at per-image cost or temporary access.'}</li>
              <li>{es ? 'Plan Premium: acceso completo y recuperación ilimitada sin costo adicional.' : 'Premium Plan: full access and unlimited recovery at no extra cost.'}</li>
            </ul>
            <p>{es ? 'La Papelera existe para que no pierdas nada por error.' : 'The Trash exists so you do not lose anything by mistake.'}</p>
          </FAQItem>

          <FAQItem testId="faq-item-premium" question={es ? '¿Cómo funciona Premium?' : 'How does Premium work?'}>
            <p>{es ? 'El Plan Premium te da:' : 'The Premium Plan gives you:'}</p>
            <ul>
              <li>{es ? 'Uso sin límites razonables de generación (política de uso justo).' : 'Reasonably unlimited generation (fair-use policy).'}</li>
              <li>{es ? 'Acceso completo a la Papelera.' : 'Full Trash access.'}</li>
              <li>{es ? 'Recuperación sin costo adicional.' : 'Recovery at no additional cost.'}</li>
              <li>{es ? 'Acceso completo al historial.' : 'Full history access.'}</li>
              <li>{es ? 'Mejor experiencia general, sin fricciones.' : 'Better overall experience, no friction.'}</li>
            </ul>
            <p>{es ? 'Premium está pensado para usuarios frecuentes o profesionales.' : 'Premium is designed for frequent or professional users.'}</p>
          </FAQItem>

          <FAQItem testId="faq-item-falkey" question={es ? '¿Cómo funciona la FAL KEY?' : 'How does the FAL KEY work?'}>
            <p>{es ? 'La FAL KEY es una clave técnica que conecta DamyxIA con servicios externos específicos (motores de generación).' : 'The FAL KEY is a technical key that connects DamyxIA with specific external services (generation engines).'}</p>
            <ul>
              <li>{es ? 'Normalmente el usuario final no necesita tocarla.' : 'End users normally do not need to touch it.'}</li>
              <li>{es ? 'Se gestiona a nivel de configuración avanzada o por el administrador.' : 'Managed in advanced settings or by the administrator.'}</li>
            </ul>
            <p>{es ? 'Si ves referencias a FAL KEY, significa que hay una integración técnica activa, pero no afecta tu uso diario.' : 'If you see FAL KEY references, it means a technical integration is active, but it does not affect your daily use.'}</p>
          </FAQItem>

          <FAQItem testId="faq-item-payments" question={es ? '¿Cómo funcionan los Pagos?' : 'How do Payments work?'}>
            <p>{es ? 'Los pagos se gestionan vía Stripe (proveedor seguro):' : 'Payments are handled via Stripe (a secure provider):'}</p>
            <ul>
              <li>{es ? 'Suscripción al Plan Premium con tarjeta.' : 'Premium Plan subscription with credit card.'}</li>
              <li>{es ? 'Facturación mensual (o según se indique).' : 'Monthly billing (or as specified).'}</li>
              <li>{es ? 'Tus datos de tarjeta NO se guardan en DamyxIA, solo en el proveedor.' : 'Your card data is NOT stored in DamyxIA, only in the provider.'}</li>
              <li>{es ? 'Podés cancelar cuando quieras desde la sección de cuenta o facturación.' : 'You can cancel anytime from account or billing.'}</li>
            </ul>
            <p>{es ? 'No hay cargos ocultos: siempre verás el precio antes de confirmar.' : 'No hidden charges: you always see the price before confirming.'}</p>
          </FAQItem>

          <FAQItem testId="faq-item-privacy" question={es ? '¿Qué pasa con mis datos e imágenes?' : 'What happens with my data and images?'}>
            <ul>
              <li>{es ? 'Se usan para que puedas ver, recuperar y organizar tu contenido.' : 'They are used so you can view, recover and organize your content.'}</li>
              <li>{es ? 'No se comparten con otros usuarios.' : 'They are not shared with other users.'}</li>
              <li>{es ? 'No se venden a terceros.' : 'They are not sold to third parties.'}</li>
              <li>{es ? 'Se almacenan con medidas de seguridad razonables.' : 'They are stored with reasonable security measures.'}</li>
            </ul>
            <p>{es ? 'El objetivo es que sientas que tu espacio en DamyxIA es tuyo y está bajo control.' : 'The goal is that your space in DamyxIA feels yours and under your control.'}</p>
          </FAQItem>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
